/* ═══════════════════════════════════════════════════════════════════════════════
   Confetti: Lightweight CSS particle animation
   
   Renders ~30 confetti particles that fall with rotation.
   Auto-cleans up after animation completes.
   Respects prefers-reduced-motion.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { useEffect, useState } from 'react';

const COLORS = [
  '#1e73be', '#3b8fd4', '#60a5fa', '#93c5fd', // brand blues
  '#06b6d4', '#22d3ee', '#67e8f9',             // teal accents
  '#10b981', '#34d399', '#6ee7b7',             // greens (success)
  '#f59e0b', '#fbbf24', '#fcd34d',             // golds
  '#3b82f6', '#60a5fa',                         // sky blues
];

const PARTICLE_COUNT = 35;

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  rotation: number;
  delay: number;
  duration: number;
  shape: 'square' | 'circle' | 'strip';
}

function generateParticles(): Particle[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: -(Math.random() * 20 + 5),
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: Math.random() * 8 + 4,
    rotation: Math.random() * 360,
    delay: Math.random() * 0.6,
    duration: Math.random() * 1.5 + 2,
    shape: (['square', 'circle', 'strip'] as const)[Math.floor(Math.random() * 3)],
  }));
}

export function Confetti() {
  const [particles] = useState(generateParticles);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="tour-confetti"
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 10002,
        overflow: 'hidden',
      }}
    >
      {particles.map((p) => {
        const shapeStyle: React.CSSProperties =
          p.shape === 'circle'
            ? { borderRadius: '50%', width: p.size, height: p.size }
            : p.shape === 'strip'
            ? { borderRadius: 2, width: p.size * 0.4, height: p.size * 1.8 }
            : { borderRadius: 2, width: p.size, height: p.size };

        return (
          <div
            key={p.id}
            className="tour-confetti-particle"
            style={{
              position: 'absolute',
              left: `${p.x}%`,
              top: `${p.y}%`,
              backgroundColor: p.color,
              ...shapeStyle,
              transform: `rotate(${p.rotation}deg)`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        );
      })}
    </div>
  );
}
